/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    UPLOAD_CONDITION_VALIDATION
 * Date:         2025-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Extracted CategorySelect from BroadcastItemRow to reduce file size and
 * improve modularity. Supports multi-select with manual review flag.
 *
 * File Size Check:
 * - New component
 * - Projected: ~150 lines
 * - Decision: Proceed (within 200-line component limit)
 */
// [2026-02-07] style-extraction: moved inline styles to SCSS module
import React, { useState, useRef, useEffect } from "react";
import { useFormikContext, getIn } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { CATEGORY_OPTIONS } from "../../../constants/inventoryOptions";
import { InventoryCategory } from "../../../interfaces/Inventory";
import styles from "../pages/broadcasts-list/BroadcastsListPage.module.scss";

interface CategorySelectProps {
  index: number;
  categoryNeedsReview?: boolean;
  onItemChange?: (index: number, field: string, value: any) => void;
}

const CategorySelect: React.FC<CategorySelectProps> = ({
  index,
  categoryNeedsReview,
  onItemChange,
}) => {
  const { values, errors, touched, handleBlur, setFieldValue } = useFormikContext<any>();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const path = `broadcast.${index}.categories`;
  const currentCategories: string[] = getIn(values, path) || [];
  const err = getIn(errors, path);
  const tch = getIn(touched, path);
  const isInvalid = Boolean(tch && err);
  const describedBy = `${path}__error`;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleCategoryToggle = (category: InventoryCategory) => {
    const current = [...currentCategories];
    if (current.includes(category)) {
      const updated = current.filter((c: string) => c !== category);
      setFieldValue(path, updated.length > 0 ? updated : undefined);
    } else {
      if (current.length < 3) {
        const updated = [...current, category];
        setFieldValue(path, updated);
        if (onItemChange) {
          onItemChange(index, "categoryNeedsReview", false);
        }
      }
    }
  };

  const handleRemoveCategory = (category: string) => {
    const updated = currentCategories.filter((c: string) => c !== category);
    setFieldValue(path, updated.length > 0 ? updated : undefined);
  };

  const availableCategories = (CATEGORY_OPTIONS as unknown as string[]).filter(
    (cat) => !currentCategories.includes(cat)
  );

  return (
    <div className="col-lg-3 col-md-6">
      <label htmlFor={`categories-${index}`} className="form-label">
        Categories ({currentCategories.length}/3)
        {categoryNeedsReview && (
          <span className="text-warning ms-1" title="Category needs manual review">
            ⚠️
          </span>
        )}
      </label>
      <div ref={dropdownRef} className="position-relative">
        <div
          className={`form-control broadcast-cats-display ${isInvalid ? "is-invalid" : ""} ${categoryNeedsReview ? "border-warning" : ""} ${styles.clickableFormControl}`}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          onBlur={handleBlur}
          aria-invalid={isInvalid}
          aria-describedby={isInvalid ? describedBy : undefined}
        >
          {currentCategories.length > 0 ? (
            currentCategories.map((cat) => (
              <span
                key={cat}
                className="badge bg-primary text-white d-inline-flex align-items-center gap-1 broadcast-cat-badge"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveCategory(cat);
                }}
              >
                {cat}
                <FontAwesomeIcon icon={faTimes} className="broadcast-cat-remove-icon" />
              </span>
            ))
          ) : (
            <span className="text-muted broadcast-cats-placeholder">
              Select categories (optional, up to 3)
            </span>
          )}
        </div>

        {isDropdownOpen && (
          <div
            className={`border rounded bg-white shadow-sm position-absolute w-100 broadcast-cats-dropdown ${styles.dropdownOverlay}`}
          >
            {availableCategories.length > 0 ? (
              availableCategories.map((category) => (
                <div
                  key={category}
                  className={`px-3 py-2 broadcast-cats-dropdown-item ${styles.clickableFormControl}`}
                  onClick={() => {
                    handleCategoryToggle(category as InventoryCategory);
                    if (currentCategories.length + 1 >= 3) {
                      setIsDropdownOpen(false);
                    }
                  }}
                >
                  {category}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-muted broadcast-cats-placeholder">
                All categories selected (max 3)
              </div>
            )}
          </div>
        )}
      </div>
      {categoryNeedsReview && (
        <div className="text-warning small mt-1">
          ⚠️ Invalid category from upload - please select valid categories
        </div>
      )}
      {isInvalid && (
        <div className="invalid-feedback d-block" id={describedBy}>
          {err as string}
        </div>
      )}
    </div>
  );
};

export default CategorySelect;

